// @tennis-matches/services/players
// Player management service for autocomplete and player database
// Tenant-aware: Players are shared within a tenant (club), not per user

import { getSupabaseClient } from "../supabase/index.js";
import {
  analyzePersonNameInput,
  foldContactSearchAscii,
  formatPersonDisplayName,
  generateId,
  scorePersonDisplayNameSimilarity,
} from "@tennis-matches/core/utils";
import { createClubPerson } from "../masterdata/people.js";

const CLUB_PLAYER_CONTACT_SIMILARITY_THRESHOLD = 0.82;

function __asStr(v) {
  return String(v ?? "").trim();
}

function normalizeClubPlayerContactRow(row) {
  const firstName = __asStr(row?.first_name);
  const lastName = __asStr(row?.last_name);
  const name =
    formatPersonDisplayName(firstName, lastName) || __asStr(row?.name);
  return {
    ...row,
    id: __asStr(row?.id),
    first_name: firstName,
    last_name: lastName,
    name,
    caption: [__asStr(row?.email), __asStr(row?.phone)]
      .filter(Boolean)
      .join(" · "),
    source: "club_people",
  };
}

function clubPlayerContactNameKey(firstName, lastName) {
  return `${foldContactSearchAscii(firstName)}\u0000${foldContactSearchAscii(lastName)}`;
}

function buildClubPeopleNameOrFilter(query) {
  const tokens = __asStr(query)
    .split(/\s+/)
    .map((token) => token.replace(/[%_]/g, ""))
    .filter(Boolean);
  const parts = new Set();
  for (const token of tokens) {
    parts.add(`first_name.ilike.%${token}%`);
    parts.add(`last_name.ilike.%${token}%`);
    parts.add(`email.ilike.%${token}%`);
    parts.add(`phone.ilike.%${token}%`);
  }
  if (tokens.length >= 2) {
    const [a, b] = tokens;
    parts.add(`and(first_name.ilike.%${a}%,last_name.ilike.%${b}%)`);
    parts.add(`and(first_name.ilike.%${b}%,last_name.ilike.%${a}%)`);
  }
  return [...parts].join(",");
}

async function getUserTenantIdViaRpc(supabase) {
  if (!supabase) return "";
  try {
    const { data, error } = await supabase.rpc("user_tenant_id");
    if (error) return "";
    const tenantId = Array.isArray(data) ? data[0] : data;
    return __asStr(tenantId);
  } catch (_) {
    return "";
  }
}

/**
 * Get current user's primary tenant ID
 * @returns {Promise<UUID|null>} - Primary tenant ID or null
 */
async function getUserPrimaryTenant() {
  console.log("🔍 [Service] getUserPrimaryTenant called");

  const supabase = await getSupabaseClient();

  // Robust check: Ensure supabase client exists and has auth property with getUser method
  if (!supabase) {
    console.error("❌ [Service] Supabase client is null/undefined");
    return null;
  }

  if (!supabase.auth || typeof supabase.auth !== "object") {
    console.error(
      "❌ [Service] Supabase client auth is not available or invalid",
    );
    console.error("❌ [Service] Client type:", typeof supabase);
    console.error(
      "❌ [Service] Client keys:",
      supabase ? Object.keys(supabase) : "N/A",
    );
    return null;
  }

  if (typeof supabase.auth.getUser !== "function") {
    console.error("❌ [Service] supabase.auth.getUser is not a function");
    console.error("❌ [Service] auth keys:", Object.keys(supabase.auth || {}));
    return null;
  }

  try {
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError) {
      console.error("❌ [Service] Auth error:", authError);
      console.error("❌ [Service] Auth error code:", authError.code);
      return null;
    }

    if (!user) {
      console.warn(
        "⚠️ [Service] No authenticated user - user may not be logged in",
      );
      return null;
    }

    console.log("👤 [Service] User ID:", user.id);

    const { data, error: tenantError } = await supabase
      .from("users")
      .select("primary_tenant_id")
      .eq("id", user.id)
      .single();

    if (tenantError) {
      console.error("❌ [Service] Error getting primary tenant:", tenantError);
      console.error("❌ [Service] Error code:", tenantError.code);
      console.error(
        "❌ [Service] Error details:",
        JSON.stringify(tenantError, null, 2),
      );
      return null;
    }

    if (!data || !data.primary_tenant_id) {
      console.warn("⚠️ [Service] User has no primary_tenant_id set");
      return null;
    }

    console.log("🏢 [Service] Primary tenant ID:", data.primary_tenant_id);
    return data.primary_tenant_id;
  } catch (err) {
    console.error("❌ [Service] Exception in getUserPrimaryTenant:", err);
    console.error("❌ [Service] Exception stack:", err.stack);
    return null;
  }
}

/**
 * Search for players by name (autocomplete)
 * ONLY returns players from the user's tenant(s) via RLS
 * Does NOT search in social.players or address lists - only the tenant-scoped players table
 *
 * NOTE: If user is not authenticated, RLS will return empty results (not an error)
 * This is expected behavior - autocomplete will simply not show suggestions for unauthenticated users
 *
 * @param {string} query - Search query
 * @param {number} limit - Maximum results (default 10)
 * @returns {Promise<Array>} - Array of player objects with {id, name, created_at}
 */
export async function searchPlayers(query, limit = 10) {
  console.log("🔍 [Service] searchPlayers called with:", { query, limit });

  const supabase = await getSupabaseClient();

  // Check if supabase is valid
  if (!supabase) {
    console.error("❌ [Service] Supabase client is null/undefined");
    return [];
  }

  if (!query || query.trim().length < 2) {
    console.log("⚠️ [Service] Query too short, returning empty");
    return [];
  }

  const trimmedQuery = query.trim();
  console.log("🔍 [Service] Trimmed query:", trimmedQuery);

  // Note: We don't explicitly check authentication here
  // RLS will handle it - if user is not authenticated, query will return empty (not error)
  // This allows the autocomplete to gracefully degrade for unauthenticated users

  // Try to check auth, but continue even if it fails (for debugging)
  try {
    if (supabase.auth && typeof supabase.auth.getUser === "function") {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();
      console.log(
        "👤 [Service] Current user:",
        user ? user.id : "NOT AUTHENTICATED",
      );

      if (authError) {
        console.error("❌ [Service] Auth error:", authError);
        console.error("❌ [Service] Auth error code:", authError.code);
      }

      if (!user) {
        console.warn(
          "⚠️ [Service] User not authenticated - RLS may block queries",
        );
      }
    } else {
      console.warn("⚠️ [Service] Auth not available on Supabase client");
      console.warn("⚠️ [Service] This will likely cause RLS policy violations");
    }
  } catch (authErr) {
    console.error("❌ [Service] Exception checking auth:", authErr);
    console.error("❌ [Service] Exception type:", typeof authErr);
  }

  // RLS automatically filters by tenant (if user is authenticated)
  // For now, try without explicit tenant filter first (RLS should handle it)
  console.log("🔍 [Service] Executing Supabase query...");

  // DEBUG: Test user_tenant_id() RPC function
  try {
    const { data: rpcTenantId, error: rpcError } =
      await supabase.rpc("user_tenant_id");
    console.log("🏢 [Service] RPC user_tenant_id() returned:", rpcTenantId);
    if (rpcError) {
      console.error("❌ [Service] RPC error:", rpcError);
      console.error(
        "❌ [Service] RPC error details:",
        JSON.stringify(rpcError, null, 2),
      );
    }
  } catch (rpcErr) {
    console.error("❌ [Service] RPC exception:", rpcErr);
    console.error(
      "❌ [Service] RPC exception details:",
      JSON.stringify(rpcErr, null, 2),
    );
  }

  // Execute fuzzy search using pg_trgm RPC function
  // This allows typo-tolerant search while respecting RLS (tenant filtering)
  // The RPC function prioritizes exact prefix matches, then similarity score
  try {
    const { data, error } = await supabase.rpc("search_players_fuzzy", {
      search_query: trimmedQuery,
      similarity_threshold: 0.2, // Minimum similarity (0.0 = no match, 1.0 = exact match)
      result_limit: limit,
    });

    if (error) {
      console.error("❌ [Service] Fuzzy search RPC error:", error);
      console.error("❌ [Service] Error code:", error.code);
      console.error("❌ [Service] Error message:", error.message);
      console.error(
        "❌ [Service] Error details:",
        JSON.stringify(error, null, 2),
      );

      // If it's an RLS violation, provide helpful message
      if (
        error.code === "42501" ||
        error.message?.includes("row-level security")
      ) {
        console.warn(
          "⚠️ [Service] RLS policy violation - user may not be authenticated or lacks tenant access",
        );
      }

      // Fallback to prefix search if RPC fails (e.g., extension not enabled)
      console.warn("⚠️ [Service] Falling back to prefix search");
      try {
        const { data: fallbackData, error: fallbackError } = await supabase
          .from("players")
          .select("id, name, created_at, tenant_id")
          .ilike("name", `${trimmedQuery}%`)
          .order("name", { ascending: true })
          .limit(limit);

        if (fallbackError) {
          console.error(
            "❌ [Service] Fallback query also failed:",
            fallbackError,
          );
          return [];
        }

        console.log(
          "✅ [Service] Fallback query successful, rows returned:",
          fallbackData?.length || 0,
        );
        return fallbackData || [];
      } catch (fallbackErr) {
        console.error("❌ [Service] Fallback query exception:", fallbackErr);
        return [];
      }
    }

    // Remove similarity_score from results (not needed in frontend)
    const results = (data || []).map(
      ({ similarity_score, ...player }) => player,
    );

    console.log(
      "✅ [Service] Fuzzy search successful, rows returned:",
      results.length,
    );
    if (results.length > 0) {
      console.log("✅ [Service] Data:", results);
      console.log(
        "✅ [Service] Player names:",
        results.map((p) => p.name),
      );
    } else {
      console.log(
        "ℹ️ [Service] No players found (may be due to RLS filtering, empty database, or low similarity threshold)",
      );
    }

    return results;
  } catch (queryErr) {
    console.error("❌ [Service] Exception during fuzzy search:", queryErr);
    console.error("❌ [Service] Exception type:", typeof queryErr);

    // Fallback to prefix search on exception
    try {
      console.warn(
        "⚠️ [Service] Falling back to prefix search after exception",
      );
      const { data: fallbackData, error: fallbackError } = await supabase
        .from("players")
        .select("id, name, created_at, tenant_id")
        .ilike("name", `${trimmedQuery}%`)
        .order("name", { ascending: true })
        .limit(limit);

      if (fallbackError) {
        console.error(
          "❌ [Service] Fallback query also failed:",
          fallbackError,
        );
        return [];
      }

      return fallbackData || [];
    } catch (fallbackErr) {
      console.error("❌ [Service] Fallback query exception:", fallbackErr);
      return [];
    }
  }
}

export async function searchClubPlayerContacts(query, limit = 10) {
  const supabase = await getSupabaseClient();
  const q = __asStr(query);
  if (!supabase || q.length < 2) return [];

  const lim = Math.min(Math.max(Number(limit) || 10, 1), 25);
  const orFilter = buildClubPeopleNameOrFilter(q);
  if (!orFilter) return [];

  try {
    const { data, error } = await supabase
      .from("club_people")
      .select("id,first_name,last_name,email,phone,tenant_id,is_inactive")
      .eq("is_inactive", false)
      .or(orFilter)
      .limit(lim * 3);

    if (error) return [];

    return (data || [])
      .map(normalizeClubPlayerContactRow)
      .filter((row) => row.id && row.name)
      .sort((a, b) => {
        const sa = scorePersonDisplayNameSimilarity(q, a);
        const sb = scorePersonDisplayNameSimilarity(q, b);
        return sb - sa || a.name.localeCompare(b.name, "de");
      })
      .slice(0, lim);
  } catch (_) {
    return [];
  }
}

async function loadClubPlayerContactsForNameCheck(limit = 500) {
  const supabase = await getSupabaseClient();
  if (!supabase) return [];
  try {
    const { data, error } = await supabase
      .from("club_people")
      .select("id,first_name,last_name,email,phone,tenant_id,is_inactive")
      .eq("is_inactive", false)
      .order("last_name", { ascending: true })
      .order("first_name", { ascending: true })
      .limit(Math.min(Math.max(Number(limit) || 500, 1), 1000));
    if (error) return [];
    return (data || [])
      .map(normalizeClubPlayerContactRow)
      .filter((row) => row.id && row.name);
  } catch (_) {
    return [];
  }
}

export async function prepareClubPlayerContactFromName(name) {
  const parsed = analyzePersonNameInput(name);
  if (!parsed.ok)
    return { ok: false, reason: parsed.reason || "invalid-name", parsed };

  const contacts = await loadClubPlayerContactsForNameCheck();
  const wantedKey = clubPlayerContactNameKey(parsed.firstName, parsed.lastName);
  const exact = contacts.find(
    (contact) =>
      clubPlayerContactNameKey(contact.first_name, contact.last_name) ===
      wantedKey,
  );
  if (exact) {
    return { ok: true, parsed, exact, similar: [] };
  }

  const similar = contacts
    .map((contact) => ({
      ...contact,
      similarity: scorePersonDisplayNameSimilarity(parsed.original, contact),
    }))
    .filter(
      (contact) =>
        contact.similarity >= CLUB_PLAYER_CONTACT_SIMILARITY_THRESHOLD,
    )
    .sort(
      (a, b) =>
        b.similarity - a.similarity || a.name.localeCompare(b.name, "de"),
    )
    .slice(0, 5);

  return { ok: true, parsed, exact: null, similar };
}

export async function createClubPlayerContactFromParsedName(parsedName) {
  const parsed =
    parsedName && typeof parsedName === "object"
      ? parsedName
      : analyzePersonNameInput(parsedName);
  if (!parsed?.ok)
    return { ok: false, reason: parsed?.reason || "invalid-name" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const tenantId = await getUserTenantIdViaRpc(supabase);
  const payload = {
    id: generateId(12),
    first_name: __asStr(parsed.firstName) || null,
    last_name: __asStr(parsed.lastName) || null,
    ...(tenantId ? { tenant_id: tenantId } : {}),
    is_inactive: false,
  };

  const res = await createClubPerson(payload);
  if (!res?.ok) return res;
  return { ok: true, data: normalizeClubPlayerContactRow(res.data) };
}

/**
 * Get all players for current user's tenant(s)
 * RLS automatically filters by tenant
 * @param {number} limit - Optional limit (default: no limit)
 * @returns {Promise<Array>} - Array of player objects
 */
export async function getAllPlayers(limit = null) {
  const supabase = await getSupabaseClient();

  if (!supabase) {
    console.error("❌ [Service] Supabase client is null/undefined");
    return [];
  }

  let query = supabase
    .from("players")
    .select("id, name, created_at, tenant_id")
    .order("name", { ascending: true });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error) {
    console.error("[players] Error fetching players:", error);
    return [];
  }

  return data || [];
}

/**
 * Add a new player to the user's primary tenant
 * Tenant ID is set automatically via trigger if not provided
 * @param {string} name - Player name
 * @returns {Promise<Object|null>} - Created player object or null
 */
export async function addPlayer(name) {
  console.log("🔍 [Service] addPlayer called with:", name);

  const supabase = await getSupabaseClient();

  if (!supabase) {
    console.error("❌ [Service] Supabase client is null/undefined");
    return null;
  }

  if (!name || name.trim().length === 0) {
    console.warn("⚠️ [Service] addPlayer: Empty name provided");
    return null;
  }

  const trimmedName = name.trim();

  try {
    // Check if user is authenticated before attempting to add player
    if (!supabase.auth || typeof supabase.auth.getUser !== "function") {
      console.warn(
        "⚠️ [Service] addPlayer: Auth not available - user may not be authenticated",
      );
      console.warn(
        "⚠️ [Service] Player will not be saved. Please ensure user is logged in.",
      );
      return null;
    }

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      console.warn("⚠️ [Service] addPlayer: User not authenticated");
      console.warn(
        "⚠️ [Service] Player will not be saved. Please log in to save players.",
      );
      return null;
    }

    // Use RPC function directly instead of querying users table
    // This avoids RLS issues with the users table
    let tenantId = null;
    try {
      const { data: rpcTenantId, error: rpcError } =
        await supabase.rpc("user_tenant_id");

      if (rpcError) {
        console.error(
          "❌ [Service] Error calling user_tenant_id() RPC:",
          rpcError,
        );
        console.error("❌ [Service] RPC error code:", rpcError.code);
        console.error(
          "❌ [Service] RPC error details:",
          JSON.stringify(rpcError, null, 2),
        );
      } else {
        // RPC can return the value directly or as a single-element array
        // Handle both cases
        const resolvedTenantId = Array.isArray(rpcTenantId)
          ? rpcTenantId[0]
          : rpcTenantId;

        if (
          resolvedTenantId &&
          typeof resolvedTenantId === "string" &&
          resolvedTenantId.length > 0
        ) {
          tenantId = resolvedTenantId;
          console.log("✅ [Service] Got tenant ID from RPC:", tenantId);
          console.log("✅ [Service] Tenant ID type:", typeof tenantId);
          console.log("✅ [Service] Tenant ID length:", tenantId.length);
        } else {
          console.warn(
            "⚠️ [Service] user_tenant_id() RPC returned invalid value:",
            rpcTenantId,
          );
          console.warn("⚠️ [Service] RPC return type:", typeof rpcTenantId);
        }
      }
    } catch (rpcErr) {
      console.error(
        "❌ [Service] Exception calling user_tenant_id() RPC:",
        rpcErr,
      );
      console.error("❌ [Service] Exception type:", typeof rpcErr);
    }

    if (!tenantId) {
      console.error(
        "❌ [Service] addPlayer: No valid tenant ID found for current user",
      );
      console.error(
        "❌ [Service] Cannot add player without tenant_id - RLS will block the insert",
      );
      console.error("❌ [Service] User ID:", user.id);
      return null;
    }

    // Validate tenant_id format (should be UUID)
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(tenantId)) {
      console.error("❌ [Service] Invalid tenant ID format:", tenantId);
      console.error("❌ [Service] Tenant ID should be a valid UUID");
      return null;
    }

    // Check if player already exists in this tenant
    // Use tenant_id filter explicitly to ensure RLS allows the query
    const { data: existing, error: checkError } = await supabase
      .from("players")
      .select("id, name, tenant_id")
      .eq("name", trimmedName)
      .eq("tenant_id", tenantId)
      .maybeSingle();

    if (checkError && checkError.code !== "PGRST116") {
      // PGRST116 is "no rows" which is fine, other errors are not
      console.warn(
        "⚠️ [Service] Error checking for existing player:",
        checkError,
      );
    }

    if (existing) {
      console.log(
        `✅ [Service] Player "${trimmedName}" already exists in tenant ${tenantId}`,
      );
      return existing;
    }

    // Insert new player
    // The trigger will set tenant_id and created_by automatically if not provided
    // We provide tenant_id explicitly as a fallback (defensive programming)
    // This ensures the insert works even if the trigger fails
    const insertData = {
      name: trimmedName,
    };

    // Provide tenant_id explicitly as fallback (trigger will also set it if NULL)
    if (tenantId) {
      insertData.tenant_id = tenantId;
      console.log(
        "🔍 [Service] Inserting player with explicit tenant_id (fallback):",
        tenantId,
      );
    } else {
      console.log(
        "⚠️ [Service] No tenant_id available - relying on trigger to set it",
      );
    }

    console.log("🔍 [Service] Inserting player with data:", insertData);

    const { data, error } = await supabase
      .from("players")
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error("❌ [Service] Error adding player:", error);
      console.error("❌ [Service] Error code:", error.code);
      console.error("❌ [Service] Error message:", error.message);
      console.error(
        "❌ [Service] Error details:",
        JSON.stringify(error, null, 2),
      );

      // Handle unique constraint violation (race condition)
      if (error.code === "23505") {
        console.log(
          "⚠️ [Service] Race condition detected, fetching just-added player",
        );
        const { data: justAdded } = await supabase
          .from("players")
          .select("id, name, tenant_id")
          .eq("name", trimmedName)
          .single();
        return justAdded;
      }

      // Handle RLS policy violation
      if (
        error.code === "42501" ||
        error.message?.includes("row-level security")
      ) {
        console.error(
          "❌ [Service] RLS policy violation - user may not be authenticated or lacks tenant access",
        );
        console.error("❌ [Service] User ID:", user?.id);
        console.error("❌ [Service] Tenant ID used:", tenantId);
        console.error("❌ [Service] Insert data:", insertData);
        console.error(
          "❌ [Service] RLS Policy requires: tenant_id = public.user_tenant_id() OR tenant_id IN tenant_memberships",
        );
        console.error(
          "❌ [Service] Ensure user is logged in and has a valid primary_tenant_id",
        );

        // Try to verify what user_tenant_id() returns at insert time
        try {
          const { data: verifyTenantId } = await supabase.rpc("user_tenant_id");
          console.error(
            "🔍 [Service] Verification: user_tenant_id() returns:",
            verifyTenantId,
          );
          console.error(
            "🔍 [Service] Verification: Does it match?",
            verifyTenantId === tenantId ||
              (Array.isArray(verifyTenantId) && verifyTenantId[0] === tenantId),
          );
        } catch (verifyErr) {
          console.error("❌ [Service] Could not verify tenant ID:", verifyErr);
        }
      }

      return null;
    }

    console.log(
      `✅ [Service] Player "${trimmedName}" added successfully:`,
      data,
    );
    return data;
  } catch (err) {
    console.error("❌ [Service] Exception in addPlayer:", err);
    return null;
  }
}

/**
 * Bulk add players to user's primary tenant
 * Handles duplicates gracefully
 * @param {Array<string>} names - Array of player names
 * @returns {Promise<Array>} - Array of created/existing players
 */
export async function addPlayers(names) {
  if (!Array.isArray(names) || names.length === 0) {
    return [];
  }

  // Remove duplicates and empty strings
  const uniqueNames = [
    ...new Set(
      names
        .filter((n) => n && typeof n === "string")
        .map((n) => n.trim())
        .filter((n) => n.length > 0),
    ),
  ];

  console.log(`[players] Adding ${uniqueNames.length} players in bulk`);

  const results = await Promise.all(uniqueNames.map((name) => addPlayer(name)));

  return results.filter(Boolean); // Remove nulls
}

/**
 * Delete a player (only if created by current user)
 * RLS policy enforces this, but we check explicitly for better UX
 * @param {string} playerId - Player UUID
 * @returns {Promise<boolean>} - Success status
 */
export async function deletePlayer(playerId) {
  console.log("🗑️ [Service] deletePlayer called with ID:", playerId);

  const supabase = await getSupabaseClient();

  if (!supabase) {
    console.error("❌ [Service] Supabase client is null/undefined");
    return false;
  }

  if (!playerId) {
    console.error("❌ [Service] deletePlayer: No player ID provided");
    return false;
  }

  // Validate UUID format
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(playerId)) {
    console.error("❌ [Service] Invalid player ID format:", playerId);
    return false;
  }

  console.log("🔍 [Service] Attempting to delete player:", playerId);

  const { error, data } = await supabase
    .from("players")
    .delete()
    .eq("id", playerId)
    .select(); // Return deleted row for confirmation

  if (error) {
    console.error("❌ [Service] Error deleting player:", error);
    console.error("❌ [Service] Error code:", error.code);
    console.error("❌ [Service] Error message:", error.message);
    return false;
  }

  if (data && data.length > 0) {
    console.log(`✅ [Service] Player deleted successfully:`, data[0]);
    return true;
  } else {
    console.warn(
      "⚠️ [Service] Player deletion returned no rows - player may not exist or RLS blocked deletion",
    );
    return false;
  }
}

/**
 * Update a player's name
 * @param {string} playerId - Player UUID
 * @param {string} newName - New player name
 * @returns {Promise<Object|null>} - Updated player or null
 */
export async function updatePlayer(playerId, newName) {
  const supabase = await getSupabaseClient();

  if (!supabase) {
    console.error("❌ [Service] Supabase client is null/undefined");
    return null;
  }

  if (!playerId || !newName || newName.trim().length === 0) {
    console.error("[players] updatePlayer: Invalid parameters");
    return null;
  }

  const { data, error } = await supabase
    .from("players")
    .update({ name: newName.trim() })
    .eq("id", playerId)
    .select()
    .single();

  if (error) {
    console.error("[players] Error updating player:", error);
    return null;
  }

  console.log(`[players] Player ${playerId} updated to "${newName.trim()}"`);
  return data;
}
